import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

export default function AdminAgreementsPage() {
  const [agreements, setAgreements] = useState([]);
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchAgreements = async () => {
      try {
        console.log('📋 Fetching rental agreements...');
        const q = query(collection(db, 'rentals'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const items = [];

        snapshot.docs.forEach((docSnap) => {
          const rental = docSnap.data();
          
          // Only include rentals that have an agreement (agreementURL exists)
          if (rental.agreementURL) {
            const item = {
              id: docSnap.id,
              customerName: rental.customerName,
              customerEmail: rental.customerEmail,
              customerPhone: rental.customerPhone,
              customerAddress: rental.customerAddress,
              carInfo: `${rental.carMake} ${rental.carModel} (${rental.carRego})`,
              carColor: rental.carColor,
              carYear: rental.carYear,
              startDate: rental.startDate?.toDate ? rental.startDate.toDate().toLocaleDateString() : 'N/A',
              endDate: rental.endDate?.toDate ? rental.endDate.toDate().toLocaleDateString() : 'N/A',
              weeklyRate: rental.weeklyRate,
              securityDeposit: rental.securityDeposit,
              licenseNumber: rental.licenseNumber,
              licenseState: rental.licenseState,
              signedAt: rental.createdAt?.toDate ? rental.createdAt.toDate().toLocaleString() : 'N/A',
              pdfUrl: rental.agreementURL,
              status: rental.status || 'active',
              returned: rental.returned || false,
              licenseFrontImageURL: rental.licenseFrontImageURL,
              licenseBackImageURL: rental.licenseBackImageURL,
            };
            items.push(item);
          }
        });

        console.log(`✅ Found ${items.length} agreements`);
        setAgreements(items);
        setFilteredAgreements(items);
      } catch (err) {
        console.error('❌ Failed to fetch agreements:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAgreements();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    filterAgreements(value, statusFilter);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    filterAgreements(searchTerm, status);
  };

  const filterAgreements = (search, status) => {
    let filtered = agreements;

    // Apply search filter
    if (search) {
      filtered = filtered.filter((a) =>
        a.customerName.toLowerCase().includes(search) ||
        a.carInfo.toLowerCase().includes(search) ||
        a.customerEmail.toLowerCase().includes(search) ||
        a.licenseNumber.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (status !== 'all') {
      if (status === 'active') {
        filtered = filtered.filter(a => a.status === 'active' && !a.returned);
      } else if (status === 'returned') {
        filtered = filtered.filter(a => a.returned);
      } else if (status === 'completed') {
        filtered = filtered.filter(a => a.status === 'completed');
      }
    }

    setFilteredAgreements(filtered);
  };

  const getStatusBadge = (status, returned) => {
    if (returned) return { text: 'Returned', color: '#10b981', bg: '#d1fae5' };
    if (status === 'active') return { text: 'Active', color: '#3b82f6', bg: '#dbeafe' };
    if (status === 'completed') return { text: 'Completed', color: '#8b5cf6', bg: '#ede9fe' };
    return { text: 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
  };

  return (
    <div style={container}>
      <div style={header}>
        <h2 style={title}>📋 Rental Agreements</h2>
        <div style={stats}>
          <span style={statItem}>Total: {agreements.length}</span>
          <span style={statItem}>Active: {agreements.filter(a => a.status === 'active' && !a.returned).length}</span>
          <span style={statItem}>Returned: {agreements.filter(a => a.returned).length}</span>
        </div>
      </div>

      <div style={filters}>
        <input
          type="text"
          placeholder="Search by customer, car, email, or license..."
          value={searchTerm}
          onChange={handleSearch}
          style={searchInput}
        />
        
        <div style={statusFilters}>
          <button 
            onClick={() => handleStatusFilter('all')} 
            style={{...statusButton, ...(statusFilter === 'all' ? activeStatusButton : {})}}
          >
            All
          </button>
          <button 
            onClick={() => handleStatusFilter('active')} 
            style={{...statusButton, ...(statusFilter === 'active' ? activeStatusButton : {})}}
          >
            Active
          </button>
          <button 
            onClick={() => handleStatusFilter('returned')} 
            style={{...statusButton, ...(statusFilter === 'returned' ? activeStatusButton : {})}}
          >
            Returned
          </button>
        </div>
      </div>

      {loading ? (
        <div style={loadingContainer}>
          <div style={spinner}></div>
          <p>Loading agreements...</p>
        </div>
      ) : filteredAgreements.length === 0 ? (
        <div style={emptyState}>
          <p>No agreements found.</p>
        </div>
      ) : (
        <div style={tableContainer}>
          {filteredAgreements.map((agreement) => {
            const statusBadge = getStatusBadge(agreement.status, agreement.returned);
            return (
              <div key={agreement.id} style={agreementCard}>
                <div style={cardHeader}>
                  <div style={customerInfo}>
                    <h3 style={customerName}>{agreement.customerName}</h3>
                    <p style={customerDetails}>
                      📧 {agreement.customerEmail} | 📞 {agreement.customerPhone}
                    </p>
                    <p style={customerAddress}>📍 {agreement.customerAddress}</p>
                  </div>
                  <div style={statusContainer}>
                    <span style={{
                      ...statusBadgeStyle,
                      backgroundColor: statusBadge.bg,
                      color: statusBadge.color
                    }}>
                      {statusBadge.text}
                    </span>
                  </div>
                </div>

                <div style={carInfo}>
                  <h4 style={carTitle}>🚗 {agreement.carInfo}</h4>
                  <p style={carDetails}>
                    Color: {agreement.carColor} | Year: {agreement.carYear} | 
                    License: {agreement.licenseNumber} ({agreement.licenseState})
                  </p>
                </div>

                <div style={rentalDetails}>
                  <div style={detailRow}>
                    <span style={detailLabel}>📅 Start Date:</span>
                    <span style={detailValue}>{agreement.startDate}</span>
                  </div>
                  <div style={detailRow}>
                    <span style={detailLabel}>📅 End Date:</span>
                    <span style={detailValue}>{agreement.endDate}</span>
                  </div>
                  <div style={detailRow}>
                    <span style={detailLabel}>💰 Weekly Rate:</span>
                    <span style={detailValue}>${agreement.weeklyRate}</span>
                  </div>
                  <div style={detailRow}>
                    <span style={detailLabel}>💳 Security Deposit:</span>
                    <span style={detailValue}>${agreement.securityDeposit}</span>
                  </div>
                  <div style={detailRow}>
                    <span style={detailLabel}>📝 Signed:</span>
                    <span style={detailValue}>{agreement.signedAt}</span>
                  </div>
                </div>

                <div style={actions}>
                  <a 
                    href={agreement.pdfUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={pdfButton}
                  >
                    📄 View Agreement
                  </a>
                  {agreement.licenseFrontImageURL && (
                    <a 
                      href={agreement.licenseFrontImageURL} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={licenseButton}
                    >
                      🪪 Front License
                    </a>
                  )}
                  {agreement.licenseBackImageURL && (
                    <a 
                      href={agreement.licenseBackImageURL} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={licenseButton}
                    >
                      🪪 Back License
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Styles
const container = {
  maxWidth: '1200px',
  margin: 'auto',
  padding: '20px',
};

const header = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
};

const title = {
  fontSize: '24px',
  fontWeight: '600',
  color: '#111827',
  margin: 0,
};

const stats = {
  display: 'flex',
  gap: '16px',
};

const statItem = {
  fontSize: '14px',
  color: '#6b7280',
  padding: '4px 12px',
  backgroundColor: '#f3f4f6',
  borderRadius: '16px',
};

const filters = {
  display: 'flex',
  gap: '16px',
  marginBottom: '24px',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const searchInput = {
  flex: 1,
  minWidth: '300px',
  padding: '12px 16px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  outline: 'none',
};

const statusFilters = {
  display: 'flex',
  gap: '8px',
};

const statusButton = {
  padding: '8px 16px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: '#fff',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const activeStatusButton = {
  backgroundColor: '#3b82f6',
  color: '#fff',
  borderColor: '#3b82f6',
};

const loadingContainer = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px',
  color: '#6b7280',
};

const spinner = {
  width: '32px',
  height: '32px',
  border: '3px solid #f3f4f6',
  borderTop: '3px solid #3b82f6',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: '16px',
};

const emptyState = {
  textAlign: 'center',
  padding: '40px',
  color: '#6b7280',
};

const tableContainer = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const agreementCard = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  padding: '24px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const cardHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '16px',
};

const customerInfo = {
  flex: 1,
};

const customerName = {
  fontSize: '18px',
  fontWeight: '600',
  color: '#111827',
  margin: '0 0 8px 0',
};

const customerDetails = {
  fontSize: '14px',
  color: '#6b7280',
  margin: '0 0 4px 0',
};

const customerAddress = {
  fontSize: '14px',
  color: '#6b7280',
  margin: 0,
};

const statusContainer = {
  marginLeft: '16px',
};

const statusBadgeStyle = {
  padding: '4px 12px',
  borderRadius: '16px',
  fontSize: '12px',
  fontWeight: '600',
};

const carInfo = {
  marginBottom: '16px',
  padding: '12px',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
};

const carTitle = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#111827',
  margin: '0 0 8px 0',
};

const carDetails = {
  fontSize: '14px',
  color: '#6b7280',
  margin: 0,
};

const rentalDetails = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '12px',
  marginBottom: '16px',
};

const detailRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const detailLabel = {
  fontSize: '14px',
  color: '#6b7280',
  fontWeight: '500',
};

const detailValue = {
  fontSize: '14px',
  color: '#111827',
  fontWeight: '500',
};

const actions = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
};

const pdfButton = {
  padding: '8px 16px',
  backgroundColor: '#3b82f6',
  color: '#fff',
  textDecoration: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'background-color 0.2s',
};

const licenseButton = {
  padding: '8px 16px',
  backgroundColor: '#10b981',
  color: '#fff',
  textDecoration: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'background-color 0.2s',
};
